import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/* ✅ Grace period before an open submenu closes, so the pointer can travel from a
   menu item down to the submenu without the menu closing underneath it. */
const SUBMENU_CLOSE_DELAY = 250;

/* ✅ Slack around the menu area, covers the transparent strip between the header
   row and the submenu card, which is clipped away and therefore not hoverable. */
const SUBMENU_AREA_PADDING = 24;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuMutationObserver = null;
  #closeClassTimer = null;
  #closeTimer = null;
  #pointer = { x: -1, y: -1 };
  #pointerTracking = false;
  #menuArea = null;
  #menuAreaTime = 0;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerleave', this.#menuPointerLeaveListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerleave', this.#menuPointerLeaveListener);
    this.#cleanupMutationObserver();
    this.#cancelCloseClassTimer();
    this.#cancelScheduledClose();
    this.#stopPointerTracking();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * ✅ The pointer left the menu element. It may still be on the way to the open
   * submenu, so only schedule a close. The pointer tracking below decides.
   */
  #menuPointerLeaveListener = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    // Pointer left the window (or the page entirely): close without a grace period.
    if (event.relatedTarget === null) {
      this.#deactivate();
      if (!this.#state.activeItem) this.#closeClasses();
      return;
    }

    this.#pointer = { x: event.clientX, y: event.clientY };
    this.#scheduleClose();
  };

  #state = {
    activeItem: null,
  };

  /**
   * ✅ While a submenu is open the pointer is tracked on the document, so the menu
   * stays open as long as the pointer is anywhere inside the menu area, no matter
   * which element the browser reports underneath it.
   */
  #startPointerTracking() {
    if (this.#pointerTracking) return;
    this.#pointerTracking = true;
    document.addEventListener('pointermove', this.#pointerMoveListener);
  }

  #stopPointerTracking() {
    if (!this.#pointerTracking) return;
    this.#pointerTracking = false;
    document.removeEventListener('pointermove', this.#pointerMoveListener);
  }

  #pointerMoveListener = (event) => {
    this.#pointer = { x: event.clientX, y: event.clientY };

    if (!this.#state.activeItem) {
      this.#stopPointerTracking();
      return;
    }

    if (this.#isPointerInsideMenuArea(event.clientX, event.clientY)) {
      this.#cancelScheduledClose();
      this.#cancelCloseClassTimer();
    } else {
      this.#scheduleClose();
    }
  };

  /**
   * The menu bar and the open submenu card, merged into a single rectangle so the
   * gap between them counts as "inside the menu".
   */
  #getMenuArea() {
    const now = performance.now();

    // Recomputed at most every 100ms: the submenu animates open, and reading
    // layout on every pointermove would be wasteful.
    if (this.#menuArea && now - this.#menuAreaTime < 100) return this.#menuArea;

    const rects = [this.getBoundingClientRect()];
    const submenu = findSubmenu(this.#state.activeItem) ?? this.overflowMenu;
    const card = submenu?.querySelector('.menu-list__submenu-inner, [part="overflow-list"]') ?? submenu;
    const cardRect = card?.getBoundingClientRect();

    if (cardRect && cardRect.height > 0) rects.push(cardRect);

    this.#menuArea = {
      top: Math.min(...rects.map((rect) => rect.top)),
      right: Math.max(...rects.map((rect) => rect.right)),
      bottom: Math.max(...rects.map((rect) => rect.bottom)),
      left: Math.min(...rects.map((rect) => rect.left)),
    };
    this.#menuAreaTime = now;

    return this.#menuArea;
  }

  #isPointerInsideMenuArea(x, y) {
    if (x < 0 || y < 0) return false;

    const area = this.#getMenuArea();

    return (
      x >= area.left - SUBMENU_AREA_PADDING &&
      x <= area.right + SUBMENU_AREA_PADDING &&
      y >= area.top - SUBMENU_AREA_PADDING &&
      y <= area.bottom + SUBMENU_AREA_PADDING
    );
  }

  #cancelScheduledClose() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  /** ✅ Close after the grace period, unless the pointer came back into the menu area. */
  #scheduleClose() {
    if (this.#closeTimer) return;

    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;

      if (this.#isPointerInsideMenuArea(this.#pointer.x, this.#pointer.y)) return;

      this.#deactivate();
    }, SUBMENU_CLOSE_DELAY);
  }

  // ✅ Helper for body class
  #toggleBodyClass(isOpen) {
    document.body.classList.toggle('submenu-open', isOpen);
  }

  #cancelCloseClassTimer() {
    if (this.#closeClassTimer) {
      clearTimeout(this.#closeClassTimer);
      this.#closeClassTimer = null;
    }
  }

  #openClasses() {
    this.#cancelCloseClassTimer();
    this.classList.add('submenu-open');
    this.#toggleBodyClass(true);
  }

  #closeClasses() {
    this.#cancelCloseClassTimer();
    this.classList.remove('submenu-open');
    this.#toggleBodyClass(false);
  }

  /**
   * ✅ Removal is deferred and skipped while the pointer is still inside the menu,
   * so moving between two menu items (or across the gap between them) keeps
   * `submenu-open` in place instead of flashing the transparent-header colors.
   */
  #scheduleCloseClasses() {
    this.#cancelCloseClassTimer();

    this.#closeClassTimer = setTimeout(() => {
      this.#closeClassTimer = null;

      if (this.#state.activeItem) return;
      if (this.matches(':hover') || this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

      this.classList.remove('submenu-open');
      this.#toggleBodyClass(false);
    }, 150);
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return this.closest('header-component');
  }

  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);
    if (!item) return;

    // ✅ Re-entering the item that is already open: just drop any pending close
    if (item == this.#state.activeItem) {
      this.#cancelScheduledClose();
      this.#cancelCloseClassTimer();
      return;
    }

    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.classList.remove('submenu-active');
    }

    this.#state.activeItem = item;

    // ✅ Add classes
    this.#cancelScheduledClose();
    this.#menuArea = null;
    this.#startPointerTracking();
    this.#openClasses();
    item.classList.add('submenu-active');

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });

      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node && menu?.contains(document.activeElement);

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(event.relatedTarget);

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // ✅ Moving straight onto another top-level item: swap submenus right away,
    // `activate` for the new item runs immediately after this.
    const relatedItem =
      event.relatedTarget instanceof Element ? event.relatedTarget.closest('.menu-list__list-item') : null;

    if (event.type !== 'pointerleave' || (relatedItem && relatedItem !== event.target)) {
      this.#deactivate();
      return;
    }

    // ✅ Otherwise the pointer only left the item itself. It can be in the gap
    // above the submenu, on the submenu card, or on its way out of the header, so
    // close after a grace period and only if it is outside the menu area by then.
    this.#pointer = { x: event.clientX, y: event.clientY };
    this.#scheduleClose();
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.#cancelScheduledClose();
    this.#stopPointerTracking();
    this.#menuArea = null;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    // ✅ Remove classes (`submenu-open` only once the pointer really left the menu)
    item.classList.remove('submenu-active');

    this.#state.activeItem = null;
    this.#scheduleCloseClasses();

    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = link.querySelector('[ref="submenu[]"]');
        if (submenu) cb(submenu);
      });
    };

    mapSubmenus((submenu) => submenu.style.setProperty('display', 'none'));
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => submenu.style.removeProperty('display'));

    return height;
  }

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? this.headerComponent.querySelector('.header__row--top')?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

// Fix typo here
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}